import React, {ReactElement} from "react";
import axios from "axios";
import store from "../redux/store";
// Concrete module, not the `redux/actions` barrel — see http.service.ts.
import {showToast} from "../redux/actions/toast.actions";
import {IToastOptions} from "../components";

// DRF 400 bodies nest arbitrarily — a field value may be a string, an array of
// strings, a nested dict (e.g. `message_data: { template: { name: "..." } }`),
// or an array of dicts. Flatten any of these into display strings so we never
// hand a raw object to React, which throws "Objects are not valid as a React
// child" and white-screens the app.
const flattenDrfError = (value: any): string[] => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) return value.flatMap(flattenDrfError);
  if (typeof value === 'object') {
    return Object.entries(value).flatMap(([key, nested]) =>
      flattenDrfError(nested).map((msg) =>
        key === 'non_field_errors' ? msg : `${key}: ${msg}`,
      ),
    );
  }
  return [String(value)];
};

export class ToastService {

  static success(message: string | ReactElement, options?: Partial<IToastOptions>) {
    ToastService.show({
      ...options,
      message,
      severity: 'success',
    });
  }

  static info(message: string | ReactElement, options?: Partial<IToastOptions>) {
    ToastService.show({
      ...options,
      message,
      severity: 'info',
    });
  }
  static warning(message: string | ReactElement, options?: Partial<IToastOptions>) {
    ToastService.show({
      ...options,
      message,
      severity: 'warning',
    });
  }
  static error(message: string | ReactElement, options?: Partial<IToastOptions>) {
    ToastService.show({
      ...options,
      message,
      severity: 'error',
    });
  }

  static show(options: IToastOptions) {
    store.dispatch(showToast(options));
  }

  static showHttpError(err, defaultMessage: string | ReactElement) {
    // Safety net: `HttpService` already swallows cancellations, but a caller
    // supplying its own abort signal can still land here. A request abandoned
    // on purpose is not a failure the user needs to hear about.
    if (axios.isCancel(err)) {
      return;
    }

    if (!axios.isAxiosError(err)) {
      ToastService.error(err?.message || defaultMessage);
      return;
    }

    const response = err?.response;
    const data = response?.data;

    if (!response) {
      ToastService.error('Network Error!');
      return;
    }

    if (!data) {
      ToastService.error(`${response.status} error - ${response.statusText}`);
      return;
    }

    if (typeof data !== 'object') {
      ToastService.error(data);
      return;
    }

    if (data.message) {
      ToastService.error(data.message);
      return;
    }

    if (response.status === 400 && !data.message) {
      const message = (
        <div>
          <div className="font-bold">Bad Request!</div>
          {Object.entries(data).map(([key, value]) => (
            <div className="flex items-start" key={key}>
              {/* DRF returns a bare list for a non-field ValidationError, and
                  Object.entries turns that into array indices -- so the toast
                  read "0: This organisation has no SDK configuration...".
                  An index is not a field name and labelling one tells the
                  reader nothing, same reason non_field_errors is unlabelled. */}
              {key !== 'non_field_errors' && !/^\d+$/.test(key) && (
                <span className="font-semibold flex-shrink-0 mr-1">{key}:</span>
              )}
              <div>
                {flattenDrfError(value).map((msg, i) => (
                  <div key={i}>{msg}</div>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
      ToastService.error(message);
      return;
    }

    ToastService.error(defaultMessage);
  }
}
